import React from 'react';
import { ForecastProject } from '@/types/forecast';
import {
  ChartBarIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface ForecastCardProps {
  forecast: ForecastProject;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  variant?: 'card' | 'list' | 'grid';
}

const TIME_UNITS = {
  HOUR: 1000 * 60 * 60,
  DAY: 1000 * 60 * 60 * 24,
  WEEK: 1000 * 60 * 60 * 24 * 7,
} as const;

const MAX_VISIBLE_TAGS = 3;

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / TIME_UNITS.HOUR);
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const ForecastCard: React.FC<ForecastCardProps> = ({
  forecast,
  onClick,
  onEdit,
  onDelete,
  variant = 'grid'
}) => {
  const resultsCount = forecast.results?.length || 0;
  const horizonYears = forecast.configuration?.forecastParams?.horizonYears || 'N/A';

  const ActionButtons = () => (
    <div className="flex space-x-1">
      {onEdit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
          title="Edit forecast"
        >
          <PencilIcon className="w-4 h-4" />
        </button>
      )}
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
          title="Delete forecast"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  if (variant === 'list') {
    return (
      <div
        className="bg-white border border-gray-200 rounded-lg p-3 hover:border-green-300 hover:bg-green-50 transition-colors cursor-pointer relative"
        onClick={onClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <ClockIcon className="w-4 h-4 text-green-600 flex-shrink-0" />

            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900 truncate">{forecast.name}</h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {horizonYears}y
                </span>
              </div>

              <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                <span>{resultsCount} periods</span>
                <span>Updated {formatDate(forecast.metadata.updatedAt)}</span>
                <span>by {forecast.metadata.userId || 'Unknown'}</span>
              </div>
            </div>
          </div>

          <ActionButtons />
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-green-300 hover:bg-green-50 transition-colors cursor-pointer relative"
      onClick={onClick}
    >
      <div className="absolute top-2 right-2">
        <ActionButtons />
      </div>

      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-medium text-gray-900 truncate">{forecast.name}</h3>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {horizonYears}y horizon
            </span>
          </div>

          <div className="text-sm text-gray-600 mb-2">
            Source: {forecast.sourceAnalysisName || 'Unknown Analysis'}
          </div>

          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <ChartBarIcon className="w-3 h-3" />
              <span>{resultsCount} periods</span>
            </div>

            <div className="flex items-center space-x-1">
              <ClockIcon className="w-3 h-3" />
              <span>{horizonYears} years</span>
            </div>

            <div className="flex items-center space-x-1">
              <UserIcon className="w-3 h-3" />
              <span>by {forecast.metadata.userId || 'Unknown'}</span>
            </div>
          </div>

          <div className="mt-2 text-xs text-gray-400">
            <span>Created {formatDate(forecast.metadata.createdAt)}</span>
            <span className="mx-2">•</span>
            <span>Updated {formatDate(forecast.metadata.updatedAt)}</span>
          </div>

          {forecast.metadata.tags && forecast.metadata.tags.length > 0 && (
            <div className="flex items-center space-x-1 mt-2">
              <TagIcon className="w-3 h-3 text-gray-400" />
              <div className="flex space-x-1">
                {forecast.metadata.tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
                {forecast.metadata.tags.length > MAX_VISIBLE_TAGS && (
                  <span className="text-gray-400 text-xs">
                    +{forecast.metadata.tags.length - MAX_VISIBLE_TAGS}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForecastCard;